/**
 * Componente LanguageSelector
 * Selector de idioma compacto (ES · EN · FR · PT) para las cabeceras.
 *
 * Decisión de diseño: son enlaces reales (no un desplegable) hacia la misma
 * página en otro idioma. Así funcionan sin JS, los buscadores descubren las
 * versiones traducidas, encaja en el menú móvil sin capas flotantes y
 * conserva el estilo tipográfico de la cabecera.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_LABELS, SUPPORTED_LANGUAGES } from '../../i18n/languages';
import { getLanguageFromPath, switchLanguagePath } from '../../i18n/paths';
import { saveLanguage } from '../../i18n/languagePreference';

/**
 * Estilos por tamaño. `sm` replica la tipografía de los enlaces de la
 * cabecera de escritorio (Lato, text-sm, mayúsculas, tracking amplio) para
 * que el selector se lea como parte de la navegación; `lg` es la variante
 * del menú móvil a pantalla completa, con áreas táctiles más grandes.
 */
const SIZE_STYLES = {
  sm: { group: 'gap-3', link: 'text-sm', separator: 'h-3' },
  lg: { group: 'gap-6', link: 'text-lg', separator: 'h-4' },
};

/**
 * @param {Object} props - Propiedades del componente
 * @param {'light'|'dark'} props.tone - `light`: texto blanco (cabecera transparente); `dark`: texto oscuro
 * @param {'sm'|'lg'} props.size - `sm`: cabecera de escritorio; `lg`: menú móvil
 * @param {string} props.className - Clases CSS adicionales
 * @returns {JSX.Element} Selector de idioma
 */
const LanguageSelector = ({ tone = 'dark', size = 'sm', className = '' }) => {
  const { pathname, search, hash } = useLocation();
  const { t } = useTranslation();
  const current = getLanguageFromPath(pathname);
  const styles = SIZE_STYLES[size];

  // Mismo color que los enlaces de la cabecera; el idioma activo se distingue
  // solo por la línea roja inferior, igual que la página activa.
  const linkColor =
    tone === 'light'
      ? 'text-white hover:text-intercasa-yellow'
      : 'text-intercasa-dark hover:text-intercasa-red';
  const separatorColor = tone === 'light' ? 'bg-white/40' : 'bg-intercasa-dark/25';

  return (
    <div role="group" aria-label={t('language.selectorLabel')} className={`flex items-center ${styles.group} ${className}`}>
      {SUPPORTED_LANGUAGES.map((lang, index) => {
        const isCurrent = lang === current;
        return (
          <React.Fragment key={lang}>
            {/* Separador fino entre idiomas, decorativo */}
            {index > 0 && <span aria-hidden="true" className={`w-px ${styles.separator} ${separatorColor}`} />}
            <Link
              to={`${switchLanguagePath(pathname, lang)}${search}${hash}`}
              hrefLang={lang}
              // Recuerda la elección (también `es`) para la próxima visita.
              onClick={() => saveLanguage(lang)}
              lang={lang}
              aria-label={LANGUAGE_LABELS[lang]}
              aria-current={isCurrent ? 'true' : undefined}
              className={`relative font-lato font-medium uppercase tracking-wider transition-colors duration-300 ${styles.link} ${linkColor}`}
            >
              {lang}
              {/* Línea del idioma activo, igual que la de la navegación */}
              <span
                aria-hidden="true"
                className={`absolute -bottom-1 left-0 h-0.5 bg-intercasa-red transition-all duration-300 ${
                  isCurrent ? 'w-full' : 'w-0'
                }`}
              />
            </Link>
          </React.Fragment>
        );
      })}
    </div>
  );
};

LanguageSelector.propTypes = {
  tone: PropTypes.oneOf(['light', 'dark']),
  size: PropTypes.oneOf(['sm', 'lg']),
  className: PropTypes.string,
};

export default LanguageSelector;
